import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiLayers, FiX, FiCheck, FiUser, FiClock, FiEye } from 'react-icons/fi';
import { templatesAPI } from '../services/api';
import { toast } from 'react-toastify';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const ModalTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ModalDescription = styled.p`
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.5;
  text-align: center;
  font-size: 10px;
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const TemplateCard = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  border: 2px solid ${props => props.selected ? '#1f2937' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f9fafb' : 'white'};
  position: relative;
  
  &:hover {
    border-color: ${props => props.selected ? '#1f2937' : '#9ca3af'};
    background: ${props => props.selected ? '#f9fafb' : '#f9fafb'};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const TemplateHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`;

const TemplateIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #6b7280;
  font-size: 20px;
  flex-shrink: 0;
`;

const TemplateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TemplateTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px 0;
  word-break: break-word;
`;

const TemplateMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const TemplateDescription = styled.p`
  font-size: 10px;
  color: #6b7280;
  margin: 0 0 12px 0;
  line-height: 1.4;
  word-break: break-word;
`;

const TemplatePreview = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  font-size: 12px;
  color: #6b7280;
  border: 1px solid #e5e7eb;
`;

const SelectionIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${props => props.selected ? '#1f2937' : 'transparent'};
  border: 2px solid ${props => props.selected ? '#1f2937' : '#d1d5db'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #6b7280;
  font-size: 32px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  ${props => props.variant === 'primary' ? `
    background: #1f2937;
    color: white;
    border: none;
    
    &:hover {
      background: #374151;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #6b7280;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      color: #374151;
    }
  `}
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #1f2937;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function TemplateSelectionModal({ isOpen, onClose, onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await templatesAPI.getAll();
      setTemplates(response.data.data);
    } catch (error) {
      toast.error('Failed to load templates');
      console.error('Template loading error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleConfirm = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
      onClose();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFieldPreview = (formStructure) => {
    if (!formStructure || !Array.isArray(formStructure)) return 'No fields';
    
    const fieldTypes = formStructure.map(field => {
      if (field.type === 'contact' && field.contactFields) {
        return `${field.type} (${field.contactFields.length} fields)`;
      }
      return field.type;
    });
    
    return fieldTypes.slice(0, 3).join(', ') + (fieldTypes.length > 3 ? '...' : '');
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FiX />
        </CloseButton>
        
        <ModalTitle>
          <FiLayers />
          Choose a Template
        </ModalTitle>
        
        <ModalDescription>
          Select a template to start creating your form quickly:
        </ModalDescription>
        
        {loading ? (
          <LoadingState>
            <LoadingSpinner />
            <p>Loading templates...</p>
          </LoadingState>
        ) : templates.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <FiLayers />
            </EmptyStateIcon>
            <EmptyStateTitle>No Templates Available</EmptyStateTitle>
            <EmptyStateDescription>
              No templates have been created yet. You can create templates by saving forms as templates in the form builder.
            </EmptyStateDescription>
          </EmptyState>
        ) : (
          <>
            <TemplatesGrid>
              {templates.map((template) => (
                <TemplateCard 
                  key={template.id}
                  selected={selectedTemplate?.id === template.id}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <SelectionIndicator selected={selectedTemplate?.id === template.id}>
                    {selectedTemplate?.id === template.id && <FiCheck />}
                  </SelectionIndicator>
                  
                  <TemplateHeader>
                    <TemplateIcon>
                      <FiLayers />
                    </TemplateIcon>
                    <TemplateInfo>
                      <TemplateTitle>{template.title}</TemplateTitle>
                      <TemplateMeta>
                        <span><FiUser /> {template.created_by}</span>
                        <span><FiClock /> {formatDate(template.created_at)}</span>
                      </TemplateMeta>
                      {template.description && (
                        <TemplateDescription>{template.description}</TemplateDescription>
                      )}
                    </TemplateInfo>
                  </TemplateHeader>
                  
                  <TemplatePreview>
                    <strong>Fields:</strong> {getFieldPreview(template.form_structure)}
                  </TemplatePreview>
                </TemplateCard>
              ))}
            </TemplatesGrid>
            
            <ButtonContainer>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleConfirm}
                disabled={!selectedTemplate}
              >
                <FiCheck />
                Use Template
              </Button>
            </ButtonContainer>
          </>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
}

export default TemplateSelectionModal;
